import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { ArrowDownToLine, ArrowUpFromLine, X, Lock, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { printHtmlSilently } from '../../lib/silentPrint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sangria (saída) e Suprimento (entrada) compartilham o mesmo modal: valor + motivo + senha do operador → comprovante 80mm
const MODES = {
  sangria: {
    title: 'Sangria de Caixa', endpoint: 'sangria', Icon: ArrowDownToLine,
    btn: 'bg-amber-600 hover:bg-amber-700', iconCls: 'text-amber-600', box: 'bg-amber-50 border-amber-200 text-amber-900',
    boxTitle: 'Retirada de dinheiro do caixa',
    boxText: 'O sistema não permitirá a sangria se o valor for maior que o saldo em dinheiro disponível. O comprovante será impresso automaticamente em 80mm após a confirmação.',
    reasonPh: 'Ex.: recolhido pelo gerente, depósito banco, cofre...', confirm: 'Confirmar Sangria', errDefault: 'Erro ao registrar sangria',
    receiptTitle: 'COMPROVANTE DE SANGRIA', receiptSub: 'Retirada de dinheiro do caixa', signer: 'Recebedor',
  },
  suprimento: {
    title: 'Suprimento de Caixa', endpoint: 'suprimento', Icon: ArrowUpFromLine,
    btn: 'bg-emerald-600 hover:bg-emerald-700', iconCls: 'text-emerald-600', box: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    boxTitle: 'Entrada de dinheiro no caixa',
    boxText: 'Use para reforço de troco ou aporte do gerente. O valor passa a compor o dinheiro esperado no fechamento. O comprovante será impresso automaticamente em 80mm.',
    reasonPh: 'Ex.: troco inicial, reforço do gerente, troca de notas...', confirm: 'Confirmar Suprimento', errDefault: 'Erro ao registrar suprimento',
    receiptTitle: 'COMPROVANTE DE SUPRIMENTO', receiptSub: 'Entrada de dinheiro no caixa', signer: 'Entregador',
  },
};

export const SangriaModal = ({ show, onClose, onSuccess, mode = 'sangria' }) => {
  const M = MODES[mode] || MODES.sangria;
  const tid = M.endpoint;
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!show) return null;

  const closeAndReset = () => {
    setAmount(''); setReason(''); setPassword(''); setError(''); setLoading(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const val = parseFloat(amount);
    if (!val || val <= 0) { setError('Valor inválido — informe um número maior que zero.'); return; }
    if (!reason || reason.trim().length < 3) { setError('Motivo obrigatório (mín. 3 caracteres).'); return; }
    if (!password) { setError('Informe a senha do operador.'); return; }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/cashflow/${M.endpoint}`, {
        amount: val,
        reason: reason.trim(),
        operator_password: password,
      }, { withCredentials: true });
      printReceipt(data, M);
      if (onSuccess) onSuccess(data);
      closeAndReset();
    } catch (err) {
      setError(err.response?.data?.detail || M.errDefault);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4" data-testid={`${tid}-modal`}>
      <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <M.Icon className={M.iconCls} size={24} />
            <h3 className="text-xl font-bold text-slate-900">{M.title}</h3>
          </div>
          <button type="button" onClick={closeAndReset} className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid={`${tid}-close`}>
            <X size={16} />
          </button>
        </div>

        <div className={`border rounded p-3 mb-4 text-xs ${M.box}`}>
          <p className="font-semibold flex items-center gap-1 mb-1"><AlertTriangle size={12} /> {M.boxTitle}</p>
          <p>{M.boxText}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Valor (R$) *</Label>
            <Input type="number" step="0.01" min="0.01" required autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" data-testid={`${tid}-amount`} className="text-lg font-bold" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Motivo *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={M.reasonPh} data-testid={`${tid}-reason`} />
            <p className="text-[10px] text-slate-500 mt-0.5">Mínimo 3 caracteres.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-700 flex items-center gap-1"><Lock size={12} /> Senha do operador *</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Sua senha" data-testid={`${tid}-password`} />
          </div>

          {error && (
            <Alert variant="destructive" data-testid={`${tid}-error`}>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={closeAndReset} className="flex-1" disabled={loading}>Cancelar</Button>
            <Button type="submit" disabled={loading} className={`flex-1 ${M.btn} text-white font-bold`} data-testid={`${tid}-confirm`}>
              <M.Icon size={16} className="mr-2" />
              {loading ? 'Registrando...' : M.confirm}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const SuprimentoModal = (props) => <SangriaModal {...props} mode="suprimento" />;

// Comprovante 80mm (térmica via Print Agent)
const printReceipt = (data, M) => {
  const dateStr = new Date(data.created_at).toLocaleString('pt-BR');
  const money = (v) => Number(v || 0).toFixed(2).replace('.', ',');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${M.receiptTitle}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; font-size: 11px; margin: 0; padding: 0; color: #000; }
  .center { text-align: center; }
  .bold { font-weight: 700; }
  .row { display: flex; justify-content: space-between; }
  hr { border: none; border-top: 1px dashed #000; margin: 4px 0; }
  .big { font-size: 13px; font-weight: 700; }
  .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #333; }
</style>
</head><body>
  <div class="center bold">================================</div>
  <div class="center bold">    ${M.receiptTitle}</div>
  <div class="center bold">================================</div>
  <div class="center"><span class="lbl">${M.receiptSub}</span></div>
  <hr/>
  <div><span class="lbl">Data/Hora:</span> ${escapeHtml(dateStr)}</div>
  <div><span class="lbl">Operador:</span> ${escapeHtml(data.operator_name || '-')}</div>
  <div><span class="lbl">Função:</span> ${escapeHtml((data.operator_role || 'operador').toUpperCase())}</div>
  <hr/>
  <div><span class="lbl">Motivo:</span></div>
  <div class="bold">${escapeHtml(data.reason || '-')}</div>
  <hr/>
  <div class="row big"><span>VALOR:</span><span>R$ ${money(data.amount)}</span></div>
  <hr/>
  <div class="row"><span class="lbl">Saldo antes:</span><span>R$ ${money(data.cash_before)}</span></div>
  <div class="row"><span class="lbl">Saldo apos:</span><span class="bold">R$ ${money(data.cash_after)}</span></div>
  <hr/>
  <div><span class="lbl">ID Movimento:</span></div>
  <div class="center" style="font-size:10px;">${escapeHtml(data.id || '-')}</div>
  <hr/>
  <div class="center">Assinatura Operador:</div>
  <br/>
  <div class="center">_______________________________</div>
  <br/>
  <div class="center">Assinatura ${M.signer}:</div>
  <br/>
  <div class="center">_______________________________</div>
  <br/>
  <div class="center bold">================================</div>
  <div class="center">Smart Flow POS</div>
  <br/><br/>
</body></html>`;
  printHtmlSilently(html);
};

const escapeHtml = (s) => String(s || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;')
  .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
